import React, { FC, useEffect, useState } from 'react'
import dayjs from 'dayjs'

import Input, { InputType } from 'components/base/forms/Input'
import Dropdown from 'components/base/forms/Dropdown'
import { safeParseEventValue } from 'common/utils/forms/safeEventParseValue'
import Icon from 'project/Icon'
import Calendar from './Calendar'
import type { CalendarProps } from 'react-calendar'
import type { Value } from 'react-calendar/dist/cjs/shared/types.js'

type DateRangePickerProps = Omit<CalendarProps, 'onChange' | 'value'> & {
  placeholder?: string
  format?: string
  inputProps?: InputType
  onChange: (v: string[] | null) => void
  value: string[] | null
  onActiveStartDateChange?: (date: Date) => void
}

const DATE_SEPARATOR = ' - '

function formatDateRange(value: string[], format: string): string {
  return `${dayjs(value[0]).format(format)}${DATE_SEPARATOR}${dayjs(
    value[1],
  ).format(format)}`
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  format = 'DD/MM/YYYY',
  inputProps,
  onActiveStartDateChange,
  placeholder,
  ...calendarProps
}) => {
  const { onChange, value, ...restCalendarProps } = calendarProps

  const [showCalendar, setShowCalendar] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [activeStartDate, setActiveStartDate] = useState<Date | undefined>(
    value?.[0] ? new Date(value[0]) : undefined,
  )

  // Sync the text input when the external value changes (and input is not focused)
  useEffect(() => {
    if (!focused && value) {
      const formatted = Array.isArray(value)
        ? formatDateRange(value, format)
        : ''
      if (formatted !== inputValue) {
        setInputValue(formatted)
      }
    }
  }, [value, focused])

  // Keep activeStartDate in sync with the external value
  useEffect(() => {
    if (value?.[0]) {
      const newStartDate = new Date(value[0])
      if (newStartDate !== activeStartDate) {
        setActiveStartDate(newStartDate)
        onActiveStartDateChange?.(newStartDate)
      }
    } else if (!value) {
      setInputValue('')
      setActiveStartDate(undefined)
    }
  }, [value])

  const handleCalendarChange = (value: Value) => {
    if (!value) {
      onChange(null)
    } else {
      const [start, end] = value as [Date, Date]
      onChange([start.toISOString(), end.toISOString()])
    }

    if (Array.isArray(value) && value[0] && value[0] !== activeStartDate) {
      const newStart = new Date(value[0])
      setActiveStartDate(newStart)
      onActiveStartDateChange?.(newStart)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = safeParseEventValue(e)
    setInputValue(str)

    const dates = str
      .split(DATE_SEPARATOR)
      .map((date: string) => dayjs(date, format))

    if (dates.length === 2 && dates[0].isValid() && dates[1].isValid()) {
      onChange([dates[0].toISOString(), dates[1].toISOString()])
    }
  }

  const iconRight = value ? (
    <div className='cursor-pointer'>
      <span className='fa fa-times-circle fs-lg' />
    </div>
  ) : (
    <Icon name='calendar' />
  )

  return (
    <div style={{ width: 220 }} className='d-flex align-items-center'>
      <div className='flex-1'>
        <Dropdown
          show={showCalendar}
          setShow={setShowCalendar}
          dropdownClassName='px-0 py-0 mt-0'
          dropdownContent={
            <Calendar
              selectRange
              showDoubleView
              activeStartDate={activeStartDate}
              onActiveStartDateChange={({ activeStartDate }) => {
                setActiveStartDate(activeStartDate ?? undefined)
              }}
              {...restCalendarProps}
              value={value as [string, string] | null}
              onChange={handleCalendarChange}
            />
          }
        >
          <Input
            onChange={handleInputChange}
            placeholder={placeholder}
            onFocus={() => {
              setFocused(true)
              setShowCalendar(true)
            }}
            onBlur={() => setFocused(false)}
            value={inputValue}
            onIconRightClick={value ? () => onChange(null) : undefined}
            iconRight={iconRight}
            {...(inputProps || {})}
          />
        </Dropdown>
      </div>
    </div>
  )
}

export default DateRangePicker
